import type {
  MaintenanceTemplateKind,
  MaintenanceTemplateListQuery,
} from '@gb-ticket/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import {
  formatMaintenanceListDate,
  maintenanceSearchFromPath,
  nextMaintenanceColumnSort,
} from '@/products/maintenance/features/maintenance-list-query';
import { MaintenanceListToolbar } from '@/products/maintenance/features/maintenance-list-toolbar';
import { MaintenanceRecordList } from '@/products/maintenance/features/maintenance-record-list';
import { MaintenanceOptionalSelect } from '@/products/maintenance/features/maintenance-filter-select';
import {
  maintenanceTemplateListPath,
  maintenanceTemplateListQueryFromSearch,
} from '@/products/maintenance/features/maintenance-template-list-path';
import {
  templateChecklistDetail,
  templateKindLabel,
} from '@/products/maintenance/features/templates/template-kind-label';
import {
  MAINTENANCE_ACTIVE_FILTER_OPTIONS,
  maintenanceActiveFromSelect,
  maintenanceActiveSelectValue,
} from '@/products/maintenance/features/maintenance-status';
import { useMaintenanceRowSelection } from '@/products/maintenance/features/maintenance-row-selection';
import {
  partitionTemplateArchive,
  partitionTemplateDelete,
} from '@/products/maintenance/features/templates/template-batch';
import {
  useDeleteMaintenanceTemplate,
  useMaintenanceTemplates,
  usePatchMaintenanceTemplate,
} from '@/products/maintenance/features/use-maintenance-templates';
import {
  maintenanceTemplateNewPath,
  maintenanceTemplatePath,
} from '@/products/maintenance/maintenance-paths';

const SEARCH_DEBOUNCE_MS = 300;

const TEMPLATE_KINDS: MaintenanceTemplateKind[] = [
  'structured',
  'pdf_overlay',
  'pdf_ink',
  'pdf_acroform',
];

function templateKindOptionLabel(kind: MaintenanceTemplateKind): string {
  const detail = templateChecklistDetail(kind);
  return detail ? `${templateKindLabel(kind)} · ${detail}` : templateKindLabel(kind);
}

function isTemplateSortBy(
  value: string,
): value is NonNullable<MaintenanceTemplateListQuery['sortBy']> {
  return value === 'name' || value === 'updatedAt';
}

function isTemplateKind(value: string): value is MaintenanceTemplateKind {
  return TEMPLATE_KINDS.some((kind) => kind === value);
}

function templateSearch(patch: Partial<MaintenanceTemplateListQuery>): string {
  return maintenanceSearchFromPath(maintenanceTemplateListPath(patch));
}

export function MaintenanceTemplatesPage() {
  usePageTitle('Vorlagen');
  const [search, setSearch] = useSearchParams();
  const listQuery = useMemo(
    () => ({
      page: 1,
      pageSize: 25,
      sortBy: 'name' as const,
      sortOrder: 'asc' as const,
      ...maintenanceTemplateListQueryFromSearch(search.toString()),
    }),
    [search],
  );
  const templates = useMaintenanceTemplates(listQuery);
  const urlQ = listQuery.q ?? '';
  const [draft, setDraft] = useState(urlQ);
  const [draftSyncKey, setDraftSyncKey] = useState(urlQ);
  if (draftSyncKey !== urlQ) {
    setDraftSyncKey(urlQ);
    setDraft(urlQ);
  }

  function apply(patch: Partial<MaintenanceTemplateListQuery>) {
    setSearch(templateSearch({ ...listQuery, ...patch, page: patch.page ?? 1 }));
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = draft.trim();
      const currentQ = listQuery.q ?? '';
      if (trimmed === currentQ) {
        return;
      }
      setSearch(templateSearch({ ...listQuery, q: trimmed || undefined, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, listQuery, setSearch]);

  function applySearchNow() {
    setSearch(templateSearch({ ...listQuery, q: draft.trim() || undefined, page: 1 }));
  }

  function resetFilters() {
    setDraft('');
    setSearch(
      templateSearch({
        page: 1,
        pageSize: listQuery.pageSize,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    );
  }

  function onColumnSort(sortBy: string) {
    if (!isTemplateSortBy(sortBy)) {
      return;
    }
    const next = nextMaintenanceColumnSort(listQuery, sortBy);
    apply(next);
  }

  const extraCount = (listQuery.kind ? 1 : 0) + (listQuery.isActive !== undefined ? 1 : 0);
  const filtered =
    Boolean(listQuery.q) ||
    extraCount > 0 ||
    listQuery.sortBy !== 'name' ||
    listQuery.sortOrder !== 'asc';
  const items = useMemo(() => templates.data?.items ?? [], [templates.data]);
  const visibleIds = useMemo(() => items.map((row) => row.id), [items]);
  const selection = useMaintenanceRowSelection(visibleIds);
  const [batch, setBatch] = useState<'delete' | 'archive' | null>(null);
  const [batchPending, setBatchPending] = useState(false);
  const removeTemplates = useDeleteMaintenanceTemplate();
  const patchTemplate = usePatchMaintenanceTemplate();
  const selectedRows = items.filter((row) => selection.selectedIds.has(row.id));
  const singleTemplate = selectedRows.length === 1 ? selectedRows[0] : undefined;
  const inactiveSelected = selectedRows.filter((row) => !row.isActive);
  const split =
    batch === 'delete'
      ? partitionTemplateDelete(selectedRows)
      : batch === 'archive'
        ? partitionTemplateArchive(selectedRows)
        : null;

  return (
    <section className="min-w-0 space-y-6">
      <PlatformPageHeader
        title="Vorlagen"
        description="Versionierte Wartungsprotokolle und PDF-Hintergründe."
        action={
          <Button className="min-h-11" asChild>
            <Link to={maintenanceTemplateNewPath()}>Vorlage anlegen</Link>
          </Button>
        }
      />

      <div className="space-y-3">
        <MaintenanceListToolbar
          extraCount={extraCount}
          filtered={filtered}
          onReset={resetFilters}
          extra={
            <>
              <MaintenanceOptionalSelect
                id="tmpl-kind"
                label="Art"
                value={listQuery.kind ?? ''}
                onValueChange={(value) =>
                  apply({ kind: value && isTemplateKind(value) ? value : undefined })
                }
                items={TEMPLATE_KINDS.map((kind) => ({
                  value: kind,
                  label: templateKindOptionLabel(kind),
                }))}
              />
              <MaintenanceOptionalSelect
                id="tmpl-active"
                label="Aktiv"
                value={maintenanceActiveSelectValue(listQuery.isActive)}
                onValueChange={(value) => apply({ isActive: maintenanceActiveFromSelect(value) })}
                items={MAINTENANCE_ACTIVE_FILTER_OPTIONS}
              />
            </>
          }
        >
          <div className="min-w-0 space-y-2">
            <Label htmlFor="tmpl-filter">Suche</Label>
            <Input
              id="tmpl-filter"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applySearchNow();
                }
              }}
              placeholder="Name"
              className="min-h-11"
            />
          </div>
        </MaintenanceListToolbar>

        {selectedRows.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-muted-foreground">{selectedRows.length} ausgewählt</p>
            {singleTemplate ? (
              <Button asChild variant="outline" className="min-h-11">
                <Link to={maintenanceTemplatePath(singleTemplate.id)}>Bearbeiten</Link>
              </Button>
            ) : (
              <Button type="button" variant="outline" className="min-h-11" disabled>
                Bearbeiten
              </Button>
            )}
            {inactiveSelected.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={batchPending}
                onClick={() => {
                  void activateSelected();
                }}
              >
                Aktivieren
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => setBatch('archive')}
            >
              Archivieren
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => setBatch('delete')}
            >
              Löschen
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11"
              onClick={() => selection.clear()}
            >
              Auswahl aufheben
            </Button>
          </div>
        ) : null}

        <MaintenanceRecordList
          rows={items}
          pending={templates.isPending ? 'Vorlagen werden geladen …' : undefined}
          error={templates.isError ? 'Die Vorlagenliste konnte nicht geladen werden.' : undefined}
          emptyLabel="Noch keine Vorlagen."
          filtered={filtered}
          onReset={resetFilters}
          getRowId={(row) => row.id}
          getHref={(row) => maintenanceTemplatePath(row.id)}
          getTitle={(row) => row.name}
          getMeta={(row) => {
            const detail = templateChecklistDetail(row.kind);
            return detail
              ? `${templateKindLabel(row.kind)} · ${detail}`
              : templateKindLabel(row.kind);
          }}
          getStatus={(row) => row.isActive}
          selection={selection}
          titleSortKey="name"
          sort={{
            sortBy: listQuery.sortBy,
            sortOrder: listQuery.sortOrder,
            onSort: onColumnSort,
          }}
          columns={[
            {
              id: 'kind',
              header: 'Art',
              cell: (row) => {
                const detail = templateChecklistDetail(row.kind);
                return detail
                  ? `${templateKindLabel(row.kind)} · ${detail}`
                  : templateKindLabel(row.kind);
              },
            },
            {
              id: 'updated',
              header: 'Geändert',
              sortKey: 'updatedAt',
              cell: (row) => formatMaintenanceListDate(row.updatedAt),
            },
          ]}
        />
        {items.length > 0 && templates.data ? (
          <MaintenanceListFooter
            page={templates.data.page}
            pageSize={templates.data.pageSize}
            total={templates.data.total}
            onPageChange={(page) => setSearch(templateSearch({ ...listQuery, page }))}
          />
        ) : null}
      </div>

      <AlertDialog
        open={batch !== null}
        onOpenChange={(open) => {
          if (!open && !batchPending) {
            setBatch(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {batch === 'archive'
                ? 'Ausgewählte Vorlagen archivieren?'
                : 'Ausgewählte Vorlagen löschen?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Abbrechen ändert nichts. Ausgeführt wird nur, was unten erlaubt ist.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {split ? (
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-medium">
                  {batch === 'archive' ? 'Wird deaktiviert' : 'Wird gelöscht'} (
                  {split.allowed.length})
                </p>
                {split.allowed.length > 0 ? (
                  <ul className="list-disc pl-5 text-muted-foreground">
                    {split.allowed.map((row) => (
                      <li key={row.id}>{row.name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">Keine.</p>
                )}
              </div>
              <div>
                <p className="font-medium">Nicht möglich ({split.rejected.length})</p>
                {split.rejected.length > 0 ? (
                  <ul className="space-y-1 text-muted-foreground">
                    {split.rejected.map(({ row, reason }) => (
                      <li key={row.id}>
                        <span className="text-foreground">{row.name}:</span> {reason}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">Keine.</p>
                )}
              </div>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={batchPending}>Abbrechen</AlertDialogCancel>
            {split && split.allowed.length > 0 ? (
              <AlertDialogAction
                disabled={batchPending}
                onClick={(event) => {
                  event.preventDefault();
                  void runBatch();
                }}
              >
                {split.rejected.length > 0 ? 'Nur erlaubte ausführen' : 'Ausführen'}
              </AlertDialogAction>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );

  async function activateSelected() {
    if (inactiveSelected.length === 0 || batchPending) {
      return;
    }
    setBatchPending(true);
    const failures: string[] = [];
    for (const row of inactiveSelected) {
      try {
        await patchTemplate.mutateAsync({ id: row.id, body: { isActive: true } });
      } catch (err: unknown) {
        const message = err instanceof ApiRequestError ? err.message : 'fehlgeschlagen';
        failures.push(`${row.name}: ${message}`);
      }
    }
    setBatchPending(false);
    selection.clear();
    if (failures.length > 0) {
      toast.error(failures.join(' '));
      return;
    }
    toast.success(
      inactiveSelected.length === 1
        ? 'Vorlage aktiviert.'
        : `${inactiveSelected.length} Vorlagen aktiviert.`,
    );
  }

  async function runBatch() {
    if (!split || split.allowed.length === 0 || batchPending) {
      return;
    }
    setBatchPending(true);
    const failures: string[] = [];
    for (const row of split.allowed) {
      try {
        if (batch === 'archive') {
          await patchTemplate.mutateAsync({ id: row.id, body: { isActive: false } });
        } else {
          await removeTemplates.mutateAsync(row.id);
        }
      } catch (err: unknown) {
        const message = err instanceof ApiRequestError ? err.message : 'fehlgeschlagen';
        failures.push(`${row.name}: ${message}`);
      }
    }
    setBatchPending(false);
    selection.clear();
    setBatch(null);
    if (failures.length > 0) {
      toast.error(failures.join(' '));
    } else {
      toast.success(batch === 'archive' ? 'Vorlagen deaktiviert.' : 'Vorlagen gelöscht.');
    }
  }
}
